import React, { useEffect } from 'react';
import logo from '../../../assets/tendera_logo_transparent.png';
import OnboardingStepper from '../OnboardingStepper/OnboardingStepper';
import './AuthLayout.css';

/**
 * AuthLayout
 *
 * Page-level shell for the entire auth + onboarding journey:
 *   - Tendera logo top-left
 *   - Onboarding stepper top-right (when showStepper is true)
 *   - Page content below
 *
 * The top bar and content share the same max-width, so the logo aligns
 * with the left edge of the page card and the stepper aligns with the
 * right edge — consistent from /login through the final onboarding step.
 *
 * Props:
 *   showStepper:      boolean — show the stepper (onboarding pages only)
 *   currentStep:      1 | 2 — required when showStepper is true
 *   contentMaxWidth:  CSS length — width of the topbar/content row, used to keep
 *                     the logo and stepper aligned with the page card edges.
 *                     Defaults to 1100px (login/register card width).
 *   children:         page content
 */
const AuthLayout = ({ showStepper = false, currentStep, contentMaxWidth, children }) => {
    const innerStyle = contentMaxWidth ? { maxWidth: contentMaxWidth } : undefined;

    // The dark surface lives on .auth-layout, but the document canvas behind it
    // is the app's default white. Any area the div doesn't paint — macOS
    // rubber-band overscroll, a repaint during scroll — flashes white on top of
    // a dark page. Tint the canvas to match for as long as an auth page is up.
    useEffect(() => {
        document.body.classList.add('auth-layout-canvas');
        return () => document.body.classList.remove('auth-layout-canvas');
    }, []);

    return (
        <div className="auth-layout">
            <div className="auth-layout__inner" style={innerStyle}>
                <header className="auth-layout__topbar">
                    <img src={logo} alt="Tendera" className="auth-layout__logo" />
                    {showStepper && (
                        <div className="auth-layout__stepper">
                            <OnboardingStepper currentStep={currentStep} />
                        </div>
                    )}
                </header>
                <main className="auth-layout__content">{children}</main>
            </div>
        </div>
    );
};

export default AuthLayout;
